import { useState } from 'react'
import { MagnifyingGlass } from '@phosphor-icons/react'
import { Banner, Button, Input } from '@/ui'
import { useTraceBackSamples } from '../../api/recall'
import { sampleStatusLabel } from '../../model/recall/words'
import { seasonLabel } from '../../model/recall/lotBalance'
import { QueryState } from '../Layout'
import { Check, Code, RefLink, Section, Status, UserName, When } from '../bits'

/**
 * Trace back samples (F7-B1.b): for a batch or pack lot, the farmer lots it came from and each
 * lot's sealed gate sample — seal, status, store place, keep-until, and whether it can be tested.
 * Looking up records a problem when a gate sample is missing or past its date, so it only runs on
 * the button.
 */
export function TraceBackPanel({ code, onCode }: { code: string; onCode: (code: string) => void }) {
  const [text, setText] = useState(code)
  const [prev, setPrev] = useState(code)
  if (code !== prev) {
    setPrev(code)
    setText(code)
  }
  const q = useTraceBackSamples(code)
  const d = q.data

  return (
    <Section
      id="trace-back"
      title="Trace back samples"
      eyebrow="Farmer’s gate samples"
      actions={
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            onCode(text.trim())
          }}
        >
          <Input
            aria-label="Batch or pack lot code"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Batch or pack lot code"
            leading={<MagnifyingGlass />}
            className="h-9 w-56 font-mono text-[14px]"
          />
          <Button type="submit" size="sm" variant="secondary" disabled={!text.trim()}>
            Trace back
          </Button>
        </form>
      }
      bodyClassName={d ? 'p-0' : undefined}
    >
      {!code ? (
        <p className="text-[14px] text-muted">
          Type a held batch or pack lot to see the farmer’s lots it came from and where each gate sample is kept.
        </p>
      ) : (
        <QueryState query={q} label="the gate samples">
          {d && (
            <div className="flex flex-col">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-rule/70 px-4 py-2 text-[14px]">
                <Code>{d.unitCode}</Code>
                <span className="text-muted">
                  {d.lots.length} farmer {d.lots.length === 1 ? 'lot' : 'lots'} · {d.farmersCount} {d.farmersCount === 1 ? 'farmer' : 'farmers'}
                </span>
              </div>
              {d.farmersCount > 1 && (
                <div className="px-4 pt-3">
                  <Banner tone="error" title="More than one farmer upstream">
                    A brand product must come from one farmer. A critical problem has been opened.{' '}
                    {d.oneFarmerExceptionId && <RefLink to={`/c/exceptions?open=${d.oneFarmerExceptionId}`}>Open the problem</RefLink>}
                  </Banner>
                </div>
              )}
              {d.lots.length === 0 ? (
                <p className="px-4 py-3 text-[14px] text-muted">No farmer lots found upstream of this code.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-[14px]">
                    <caption className="sr-only">Farmer lots and gate samples</caption>
                    <thead>
                      <tr className="text-left font-mono text-[10.5px] font-bold uppercase tracking-label text-muted">
                        <th scope="col" className="px-4 py-2">Farmer lot</th>
                        <th scope="col" className="px-2 py-2">Farmer</th>
                        <th scope="col" className="px-2 py-2">Gate sample seal</th>
                        <th scope="col" className="px-2 py-2">Status</th>
                        <th scope="col" className="hidden px-2 py-2 md:table-cell">Kept at</th>
                        <th scope="col" className="hidden px-2 py-2 sm:table-cell">Keep until</th>
                        <th scope="col" className="px-4 py-2">Can be tested</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.lots.map((l) => (
                        <tr key={l.unitId} className="border-t border-rule/60 align-top">
                          <td className="px-4 py-2">
                            <RefLink to={`/c/lots/${l.unitId}`}>{l.code}</RefLink>
                            <div className="text-[12px] text-muted">{seasonLabel(l.seasonCode)}</div>
                          </td>
                          <td className="px-2 py-2">
                            <UserName id={l.farmerId} />
                          </td>
                          <td className="px-2 py-2">{l.sample ? <Code>{l.sample.seal}</Code> : <span className="text-danger">No gate sample</span>}</td>
                          <td className="px-2 py-2">{l.sample ? <Status status={l.sample.status} label={sampleStatusLabel(l.sample.status)} /> : '—'}</td>
                          <td className="hidden px-2 py-2 md:table-cell">{l.sample?.locationPath ?? l.sample?.locationCode ?? <span className="text-muted">Not recorded</span>}</td>
                          <td className="hidden px-2 py-2 sm:table-cell">
                            <When at={l.sample?.retainUntil} dateOnly />
                          </td>
                          <td className="px-4 py-2">
                            <span className="flex flex-col gap-0.5">
                              <Check ok={l.sampleUsable} label={l.sampleUsable ? 'Yes' : 'No'} />
                              {l.exceptionId && (
                                <RefLink to={`/c/exceptions?open=${l.exceptionId}`} className="font-sans text-[12.5px]">
                                  Problem recorded
                                </RefLink>
                              )}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </QueryState>
      )}
    </Section>
  )
}
